import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatMap, map, catchError, of, mergeMap, switchMap } from "rxjs";
import { AttioClient } from "src/api-service";
import { WebhookActions } from "./webhooks.actions";

@Injectable()
export class WebhooksEffects {
    private readonly actions$ = inject(Actions);
    private readonly attioClient = inject(AttioClient);

    loadWebhooks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WebhookActions.loadWebhooks),
            switchMap(() =>
                this.attioClient.getWebhooks().pipe(
                    map(response => WebhookActions.loadWebhooksSuccess({ webhooks: response?.data ?? [] })),
                    catchError(error => of(WebhookActions.loadWebhooksFailure({ error }))),
                ),
            ),
        ),
    );

    getWebhook$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WebhookActions.getWebhook),
            switchMap(({ webhookId }) =>
                this.attioClient.getWebhook(webhookId).pipe(
                    map(response => WebhookActions.getWebhookSuccess({ webhook: response?.data })),
                    catchError(error => of(WebhookActions.getWebhookFailure({ error }))),
                ),
            ),
        ),
    );

    createWebhook$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WebhookActions.createWebhook),
            concatMap(({ body }) =>
                this.attioClient.createWebhook(body).pipe(
                    map(() =>
                        WebhookActions.createWebhookSuccess({
                            toastKey: "ADMIN.DEVELOPER.WEBHOOK.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(WebhookActions.createWebhookFailure({
                            error,
                            toastKey: "ADMIN.DEVELOPER.WEBHOOK.TOAST.CREATE_FAILURE",
                        })),
                    ),
                ),
            ),
        ),
    );

    updateWebhook$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WebhookActions.updateWebhook),
            concatMap(({ webhookId, body }) =>
                this.attioClient.updateWebhook(webhookId, body).pipe(
                    map(() =>
                        WebhookActions.updateWebhookSuccess({
                            toastKey: "ADMIN.DEVELOPER.WEBHOOK.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(WebhookActions.updateWebhookFailure({
                            error,
                            toastKey: "ADMIN.DEVELOPER.WEBHOOK.TOAST.UPDATE_FAILURE",
                        })),
                    ),
                ),
            ),
        ),
    );

    deleteWebhook$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WebhookActions.deleteWebhook),
            mergeMap(({ webhookId }) =>
                this.attioClient.deleteWebhook(webhookId).pipe(
                    map(() =>
                        WebhookActions.deleteWebhookSuccess({
                            webhookId,
                            toastKey: "ADMIN.DEVELOPER.WEBHOOK.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(WebhookActions.deleteWebhookFailure({
                            error,
                            toastKey: "ADMIN.DEVELOPER.WEBHOOK.TOAST.DELETE_FAILURE",
                        })),
                    ),
                ),
            ),
        ),
    );

    refreshAfterMutation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WebhookActions.createWebhookSuccess, WebhookActions.deleteWebhookSuccess, WebhookActions.updateWebhookSuccess),
            map(() => WebhookActions.loadWebhooks()),
        ),
    );
}
